/**
 * Общие утилиты ALKOR. Используются и в API, и в Mini App, и в боте —
 * поэтому здесь нет ни браузерных, ни серверных зависимостей.
 */

const NBSP = ' ';

/**
 * 21900 → "21 900 ₽" с неразрывными пробелами.
 * Точка как разделитель тысяч в русской типографике читается как дробная часть.
 */
export function formatPrice(value: number, currency = '₽'): string {
  const rounded = Math.round(value);
  const grouped = String(Math.abs(rounded)).replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
  const sign = rounded < 0 ? '−' : '';
  return `${sign}${grouped}${NBSP}${currency}`;
}

/**
 * Цена 0 — товар без цены. В базе он хранится с price = 0, покупателю
 * показывается «Цена по запросу», в корзину и заказ такой товар не попадает.
 */
export function isPriceOnRequest(value: number | string | null | undefined): boolean {
  return !(Number(value) > 0);
}

/**
 * Цена для витрины и бота: «21 900 ₽» или «Цена по запросу».
 * Суммы корзин и заказов сюда не передавать — это только цена товара.
 */
export function formatPriceOrRequest(
  value: number | string | null | undefined,
  requestText = 'Цена по запросу',
  currency = '₽',
): string {
  return isPriceOnRequest(value) ? requestText : formatPrice(Number(value), currency);
}

/** 21900 → "21 900" без валюты — для полей ввода и таблиц. */
export function formatNumber(value: number): string {
  return String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
}

/** Процент скидки для бейджа: 24900 → 21900 даёт 12. */
export function discountPercent(price: number, oldPrice: number | null): number | null {
  if (!oldPrice || oldPrice <= price) return null;
  return Math.round((1 - price / oldPrice) * 100);
}

/** "01.09.2026" — для прайса и списка заказов. */
export function formatDate(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
}

const MONTHS = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

/** "14 августа 2026" · "Сегодня" · "Вчера" */
export function formatDateHuman(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  const today = new Date();
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(today) - startOf(d)) / 86_400_000);
  if (diffDays === 0) return 'Сегодня';
  if (diffDays === 1) return 'Вчера';
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** "3 товара" — правильная форма русского слова по числу. */
export function plural(n: number, one: string, few: string, many: string): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return many;
  if (last > 1 && last < 5) return few;
  if (last === 1) return one;
  return many;
}

export function pluralize(n: number, one: string, few: string, many: string): string {
  return `${formatNumber(n)}${NBSP}${plural(n, one, few, many)}`;
}

// ─── Телефон ────────────────────────────────────────────────────────────────

/** Оставляет только цифры и приводит 8XXXXXXXXXX к 7XXXXXXXXXX. */
export function normalizePhone(input: string): string {
  let digits = input.replace(/\D/g, '');
  if (digits.startsWith('8') && digits.length === 11) digits = '7' + digits.slice(1);
  if (digits.length === 10) digits = '7' + digits;
  return digits;
}

export function isValidPhone(input: string): boolean {
  const d = normalizePhone(input);
  return d.length === 11 && d.startsWith('7');
}

/** Маска ввода: 79001234567 → "+7 (900) 123-45-67". */
export function formatPhone(input: string): string {
  const d = normalizePhone(input).slice(0, 11);
  if (!d) return '';
  const parts = [
    '+7',
    d.length > 1 ? ` (${d.slice(1, 4)}` : '',
    d.length >= 4 ? ')' : '',
    d.length > 4 ? ` ${d.slice(4, 7)}` : '',
    d.length > 7 ? `-${d.slice(7, 9)}` : '',
    d.length > 9 ? `-${d.slice(9, 11)}` : '',
  ];
  return parts.join('');
}

// ─── Deep links ─────────────────────────────────────────────────────────────

export type DeepLink =
  | { kind: 'product'; slug: string }
  | { kind: 'category'; slug: string }
  | { kind: 'search'; query: string }
  | { kind: 'cart' }
  | { kind: 'orders' }
  | { kind: 'order'; number: string }
  | { kind: 'notification'; id: string }
  | { kind: 'utm'; source: string; campaign: string };

/**
 * Telegram допускает в payload только [A-Za-z0-9_-] и 64 символа.
 * Поисковый запрос кодируется base64url, при переполнении режется до 3 слов.
 */
export function encodeDeepLink(link: DeepLink): string {
  switch (link.kind) {
    case 'product':  return `p_${link.slug}`;
    case 'category': return `c_${link.slug}`;
    case 'cart':     return 'cart';
    case 'orders':   return 'orders';
    case 'order':    return `o_${link.number}`;
    case 'notification': return `n_${link.id}`;
    case 'utm':      return `utm_${link.source}_${link.campaign}`;
    case 'search': {
      const trim = (q: string) => toBase64Url(q);
      let payload = trim(link.query);
      if (payload.length > 60) payload = trim(link.query.split(/\s+/).slice(0, 3).join(' '));
      return `s_${payload.slice(0, 60)}`;
    }
  }
}

export function decodeDeepLink(payload: string | null | undefined): DeepLink | null {
  if (!payload) return null;
  if (payload === 'cart') return { kind: 'cart' };
  if (payload === 'orders') return { kind: 'orders' };
  const rest = payload.slice(2);
  if (payload.startsWith('p_')) return { kind: 'product', slug: rest };
  if (payload.startsWith('c_')) return { kind: 'category', slug: rest };
  if (payload.startsWith('o_')) return { kind: 'order', number: rest };
  if (payload.startsWith('n_')) return { kind: 'notification', id: rest };
  if (payload.startsWith('s_')) {
    const query = fromBase64Url(rest);
    return query ? { kind: 'search', query } : null;
  }
  if (payload.startsWith('utm_')) {
    const [source = '', campaign = ''] = payload.slice(4).split('_');
    return { kind: 'utm', source, campaign };
  }
  return null;
}

/** Маршрут Mini App для распознанной ссылки. Должен совпадать с routes.tsx. */
export function deepLinkToPath(link: DeepLink): string {
  switch (link.kind) {
    case 'product':  return `/p/${link.slug}`;
    case 'category': return `/c/${link.slug}`;
    case 'search':   return `/search?q=${encodeURIComponent(link.query)}`;
    case 'cart':     return '/cart';
    case 'orders':   return '/orders';
    case 'order':    return `/orders/${link.number}`;
    default:         return '/';
  }
}

/**
 * base64url без Buffer.
 *
 * Пакет общий для браузера и Node, поэтому здесь нет ни Buffer, ни btoa как
 * обязательной зависимости: в браузере и в Node ≥16 берётся встроенный
 * btoa/atob, а чистая реализация ниже — страховка для остальных сред.
 */
const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

function bytesToBase64(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i]!, b = bytes[i + 1], c = bytes[i + 2];
    const n = (a << 16) | ((b ?? 0) << 8) | (c ?? 0);
    out += B64[(n >> 18) & 63]! + B64[(n >> 12) & 63]!
      + (b === undefined ? '=' : B64[(n >> 6) & 63]!)
      + (c === undefined ? '=' : B64[n & 63]!);
  }
  return out;
}

function base64ToBytes(b64: string): Uint8Array {
  const clean = b64.replace(/=+$/, '');
  const out = new Uint8Array((clean.length * 3) >> 2);
  let acc = 0, bits = 0, p = 0;
  for (const ch of clean) {
    const v = B64.indexOf(ch);
    if (v < 0) throw new Error('bad base64');
    acc = (acc << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out[p++] = (acc >> bits) & 0xff;
    }
  }
  return out.subarray(0, p);
}

function toBase64Url(s: string): string {
  const bytes = new TextEncoder().encode(s);
  const b64 = typeof btoa === 'function'
    ? btoa(String.fromCharCode(...bytes))
    : bytesToBase64(bytes);
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(s: string): string | null {
  try {
    const b64 = s.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(s.length / 4) * 4, '=');
    const bytes = typeof atob === 'function'
      ? Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))
      : base64ToBytes(b64);
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

// ─── Разное ─────────────────────────────────────────────────────────────────

export function debounce<A extends unknown[]>(fn: (...a: A) => void, ms: number) {
  let t: ReturnType<typeof setTimeout> | undefined;
  return (...args: A) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

/** Экранирование для Telegram MarkdownV2 — без него имена с дефисом ломают сообщение. */
export function escapeMarkdownV2(text: string): string {
  return text.replace(/[_*[\]()~`>#+\-=|{}.!\\]/g, (c) => `\\${c}`);
}

export const AVAILABILITY_LABEL: Record<string, string> = {
  in_stock: 'В наличии',
  on_order: 'Под заказ',
  expected: 'Ожидается',
  out_of_stock: 'Нет в наличии',
};

export const ORDER_STATUS_LABEL: Record<string, string> = {
  new: 'Новый',
  manager_contact: 'Связались',
  confirmed: 'Подтверждён',
  processing: 'В работе',
  shipped: 'Отправлен',
  completed: 'Выполнен',
  cancelled: 'Отменён',
};

export const DELIVERY_LABEL: Record<string, string> = {
  pickup: 'Самовывоз',
  courier: 'Курьер',
  shipping: 'Транспортная компания',
};
